/**
 * Automotive Industry Configuration
 *
 * Supports: Assembly Robots, Welding Equipment, Paint Systems, Presses, Conveyors, Test Equipment
 */

import { IndustryConfig } from '../industryTypes'

export const automotiveConfig: IndustryConfig = {
  id: 'automotive',
  name: 'Automotive',
  description: 'Vehicle assembly and manufacturing operations',
  icon: 'Car',

  terminology: {
    asset: 'Equipment',
    assets: 'Equipment',
    assetPlural: 'Equipment',
    assetType: 'Equipment Type',
    failure: 'Stoppage',
    health: 'Condition',
    healthScore: 'Health Score',
    healthy: 'Operational',
    degraded: 'Needs Attention',
    critical: 'Line Down',
    alert: 'Andon',
    process: 'Assembly',
    maintenance: 'Maintenance',
    inspection: 'Inspection',
    downtime: 'Downtime',
    workOrder: 'Work Order',
    prediction: 'Forecast',
    anomaly: 'Abnormality',
    trend: 'Trend',
    risk: 'Risk',
    facility: 'Plant',
    facilityPlural: 'Plants',
    location: 'Station',
    operator: 'Team Member',
    supervisor: 'Group Leader',
    calibration: 'Calibration',
    compliance: 'IATF Compliance',
    criticality: 'Criticality',
  },

  assetTypes: [
    {
      id: 'assembly_robot',
      name: 'Assembly Robot',
      icon: 'Bot',
      description: 'Industrial robots for assembly operations',
      category: 'Body Shop',
      defaultMetrics: ['cycle_time', 'positioning_accuracy', 'torque', 'temperature'],
      maintenanceTypes: ['lubrication', 'calibration', 'servo_service', 'major_overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'In-Line', color: '#ef4444', description: 'In-line robot (stops production)', downtimeCostMultiplier: 2000 },
        { id: 'high', name: 'Sub-Assembly', color: '#f59e0b', description: 'Sub-assembly robot', downtimeCostMultiplier: 800 },
        { id: 'medium', name: 'Parallel', color: '#3b82f6', description: 'Parallel operation (buffer exists)', downtimeCostMultiplier: 300 },
        { id: 'low', name: 'Offline', color: '#6b7280', description: 'Offline operation', downtimeCostMultiplier: 50 },
      ],
    },
    {
      id: 'spot_welder',
      name: 'Spot Welder',
      icon: 'Zap',
      description: 'Resistance spot welding equipment',
      category: 'Body Shop',
      defaultMetrics: ['weld_current', 'tip_resistance', 'force', 'tip_life'],
      maintenanceTypes: ['tip_dress', 'tip_change', 'transformer_service', 'cable_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Safety Critical', color: '#ef4444', description: 'Safety-critical welds', downtimeCostMultiplier: 3000 },
        { id: 'high', name: 'Structural', color: '#f59e0b', description: 'Structural welds', downtimeCostMultiplier: 1500 },
        { id: 'medium', name: 'Non-Structural', color: '#3b82f6', description: 'Non-structural welds', downtimeCostMultiplier: 500 },
        { id: 'low', name: 'Cosmetic', color: '#6b7280', description: 'Cosmetic welds', downtimeCostMultiplier: 100 },
      ],
    },
    {
      id: 'paint_robot',
      name: 'Paint Robot',
      icon: 'Paintbrush',
      description: 'Robotic paint application systems',
      category: 'Paint Shop',
      defaultMetrics: ['film_thickness', 'color_match', 'flow_rate', 'atomization'],
      maintenanceTypes: ['bell_cleaning', 'pump_service', 'calibration', 'pattern_adjustment'],
      criticalityLevels: [
        { id: 'critical', name: 'Primer', color: '#ef4444', description: 'Primer application', downtimeCostMultiplier: 2500 },
        { id: 'high', name: 'Base Coat', color: '#f59e0b', description: 'Base coat application', downtimeCostMultiplier: 2000 },
        { id: 'medium', name: 'Clear Coat', color: '#3b82f6', description: 'Clear coat application', downtimeCostMultiplier: 1500 },
        { id: 'low', name: 'Touch-Up', color: '#6b7280', description: 'Touch-up station', downtimeCostMultiplier: 300 },
      ],
    },
    {
      id: 'stamping_press',
      name: 'Stamping Press',
      icon: 'ArrowDownCircle',
      description: 'Metal stamping presses',
      category: 'Stamping',
      defaultMetrics: ['tonnage', 'stroke_count', 'slide_parallelism', 'lubrication_flow'],
      maintenanceTypes: ['die_maintenance', 'lubrication', 'clutch_brake_service', 'major_overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Transfer Line', color: '#ef4444', description: 'Transfer press line', downtimeCostMultiplier: 5000 },
        { id: 'high', name: 'Tandem Line', color: '#f59e0b', description: 'Tandem press line', downtimeCostMultiplier: 3000 },
        { id: 'medium', name: 'Blanking', color: '#3b82f6', description: 'Blanking press', downtimeCostMultiplier: 1000 },
        { id: 'low', name: 'Tryout', color: '#6b7280', description: 'Tryout press', downtimeCostMultiplier: 200 },
      ],
    },
    {
      id: 'conveyor_system',
      name: 'Conveyor System',
      icon: 'ArrowRightLeft',
      description: 'Assembly line conveyors',
      category: 'Material Handling',
      defaultMetrics: ['speed', 'chain_wear', 'motor_current', 'position_accuracy'],
      maintenanceTypes: ['chain_lubrication', 'carrier_inspection', 'motor_service', 'track_alignment'],
      criticalityLevels: [
        { id: 'critical', name: 'Main Line', color: '#ef4444', description: 'Main assembly conveyor', downtimeCostMultiplier: 4000 },
        { id: 'high', name: 'Paint', color: '#f59e0b', description: 'Paint shop conveyor', downtimeCostMultiplier: 2500 },
        { id: 'medium', name: 'Sub-Assembly', color: '#3b82f6', description: 'Sub-assembly conveyor', downtimeCostMultiplier: 800 },
        { id: 'low', name: 'Parts', color: '#6b7280', description: 'Parts conveyor', downtimeCostMultiplier: 200 },
      ],
    },
    {
      id: 'test_equipment',
      name: 'Test Equipment',
      icon: 'ClipboardCheck',
      description: 'End-of-line test equipment',
      category: 'Final Assembly',
      defaultMetrics: ['test_cycle_time', 'first_pass_rate', 'calibration_status', 'repeatability'],
      maintenanceTypes: ['calibration', 'sensor_replacement', 'software_update', 'fixture_service'],
      criticalityLevels: [
        { id: 'critical', name: 'Safety Test', color: '#ef4444', description: 'Safety testing equipment', downtimeCostMultiplier: 3000 },
        { id: 'high', name: 'Powertrain', color: '#f59e0b', description: 'Powertrain dyno', downtimeCostMultiplier: 2000 },
        { id: 'medium', name: 'ADAS', color: '#3b82f6', description: 'ADAS calibration', downtimeCostMultiplier: 1000 },
        { id: 'low', name: 'Cosmetic', color: '#6b7280', description: 'Cosmetic inspection', downtimeCostMultiplier: 200 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'production_performance',
      name: 'Production Performance',
      icon: 'TrendingUp',
      description: 'Assembly line efficiency metrics',
      businessValue: 'Maximize production output and meet delivery schedules',
      category: 'universal',
      metrics: [
        { id: 'oee', name: 'OEE', unit: '%', description: 'Overall Equipment Effectiveness', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 75, critical: 65 } },
        { id: 'jpph', name: 'JPPH', unit: 'jobs/hour', description: 'Jobs Per Person Hour', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 1.0, warning: 0.9, critical: 0.8 } },
        { id: 'takt_attainment', name: 'Takt Attainment', unit: '%', description: 'Meeting takt time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 75, max: 85 }, critical: { max: 75 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'quality_metrics',
      name: 'Quality Metrics',
      icon: 'CheckSquare',
      description: 'First-time-through and defect metrics',
      businessValue: 'Reduce rework, warranties, and recalls',
      category: 'universal',
      metrics: [
        { id: 'ftt', name: 'FTT', unit: '%', description: 'First Time Through', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 98, warning: 95, critical: 90 } },
        { id: 'dpu', name: 'DPU', unit: 'defects/unit', description: 'Defects Per Unit', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 1.0, critical: 2.0 } },
        { id: 'r_100', name: 'R/100', unit: 'repairs/100', description: 'Repairs per 100 vehicles', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 5, warning: 10, critical: 20 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 95, max: 98 }, critical: { max: 95 } },
      visualizations: ['gauge', 'pareto', 'control_chart', 'trend'],
    },
    {
      id: 'weld_quality',
      name: 'Weld Quality',
      icon: 'Zap',
      description: 'Welding process metrics',
      businessValue: 'Ensure structural integrity and safety',
      category: 'industry_specific',
      metrics: [
        { id: 'weld_rate', name: 'Weld Success Rate', unit: '%', description: 'Successful welds', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99.9, warning: 99.5, critical: 99.0 } },
        { id: 'nugget_diameter', name: 'Nugget Diameter', unit: 'mm', description: 'Average weld nugget size', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 5.0, warning: 4.5, critical: 4.0 } },
        { id: 'tip_life', name: 'Tip Life', unit: 'welds', description: 'Tip life before dress', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 500, warning: 400, critical: 300 } },
      ],
      thresholds: { healthy: { min: 99.9 }, warning: { min: 99.0, max: 99.9 }, critical: { max: 99.0 } },
      visualizations: ['control_chart', 'histogram', 'trend', 'scatter'],
    },
    {
      id: 'paint_quality',
      name: 'Paint Quality',
      icon: 'Paintbrush',
      description: 'Paint shop quality metrics',
      businessValue: 'Achieve flawless finish and customer satisfaction',
      category: 'industry_specific',
      metrics: [
        { id: 'seeds_per_unit', name: 'Seeds Per Unit', unit: 'count', description: 'Paint defects per unit', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 1.0, critical: 2.0 } },
        { id: 'color_match', name: 'Color Match dE', unit: 'dE', description: 'Delta E color deviation', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.5, warning: 1.0, critical: 1.5 } },
        { id: 'film_build', name: 'Film Build', unit: 'microns', description: 'Total film thickness', dataType: 'continuous', aggregation: 'avg', goodDirection: 'target', defaultThresholds: { healthy: 110, warning: 100, critical: 90 } },
      ],
      thresholds: { healthy: { max: 0.5 }, warning: { min: 0.5, max: 1.0 }, critical: { min: 1.0 } },
      visualizations: ['control_chart', 'heatmap', 'trend', 'distribution'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'robot_pm',
      name: 'Robot Preventive Maintenance',
      description: 'Regular robot maintenance',
      category: 'preventive',
      defaultInterval: { value: 3, unit: 'months' },
      laborRequirements: [
        { role: 'Robot Technician', count: 1, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 2, typical: 4, max: 8, unit: 'hours' },
      skillsRequired: ['robot_programming', 'mechanical_aptitude'],
      partsCommonly: ['lubricants', 'filters', 'seals'],
    },
    {
      id: 'tip_dress',
      name: 'Weld Tip Dressing',
      description: 'Automated tip dressing cycle',
      category: 'condition_based',
      defaultInterval: { value: 500, unit: 'cycles' },
      laborRequirements: [
        { role: 'Weld Technician', count: 1, hourlyRate: 55 },
      ],
      estimatedDuration: { min: 5, typical: 10, max: 20, unit: 'minutes' },
      skillsRequired: ['welding_fundamentals'],
      partsCommonly: ['tip_dresser_blades'],
    },
    {
      id: 'die_maintenance',
      name: 'Die Maintenance',
      description: 'Stamping die cleaning and repair',
      category: 'preventive',
      defaultInterval: { value: 50000, unit: 'strokes' },
      laborRequirements: [
        { role: 'Die Maker', count: 2, hourlyRate: 70 },
        { role: 'Press Operator', count: 1, hourlyRate: 50 },
      ],
      estimatedDuration: { min: 4, typical: 8, max: 16, unit: 'hours' },
      skillsRequired: ['die_making', 'press_operation'],
      partsCommonly: ['die_springs', 'wear_plates', 'punches'],
    },
  ],

  riskFactors: [
    { id: 'line_stoppage_risk', name: 'Line Stoppage Risk', description: 'Risk of production line stoppage', category: 'operational', weight: 0.40, calculationMethod: 'exponential', inputs: ['health_score', 'criticality', 'buffer_status'] },
    { id: 'quality_risk', name: 'Quality Risk', description: 'Risk of quality defects', category: 'quality', weight: 0.30, calculationMethod: 'linear', inputs: ['process_capability', 'tool_condition'] },
    { id: 'safety_risk', name: 'Safety Risk', description: 'Risk to team member safety', category: 'safety', weight: 0.20, calculationMethod: 'threshold', inputs: ['guarding_status', 'maintenance_due'] },
    { id: 'recall_risk', name: 'Recall Risk', description: 'Risk of safety recall', category: 'compliance', weight: 0.10, calculationMethod: 'threshold', inputs: ['safety_test_results', 'traceability'] },
  ],

  complianceStandards: [
    {
      id: 'iatf_16949',
      name: 'IATF 16949',
      shortName: 'IATF',
      description: 'Automotive quality management system',
      authority: 'IATF',
      region: ['Global'],
      requirements: [
        { id: 'control_plan', name: 'Control Plans', description: 'Documented control plans', kpiId: 'control_plans_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'msa', name: 'MSA Studies', description: 'Measurement system analysis', kpiId: 'msa_current', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
    {
      id: 'iso_14001',
      name: 'ISO 14001',
      shortName: 'EMS',
      description: 'Environmental management system',
      authority: 'ISO',
      region: ['Global'],
      requirements: [
        { id: 'env_aspects', name: 'Environmental Aspects', description: 'Identified and controlled', kpiId: 'aspects_controlled', threshold: 100, operator: 'eq', severity: 'required' },
        { id: 'waste_reduction', name: 'Waste Reduction', description: 'Meeting reduction targets', kpiId: 'waste_target', threshold: 100, operator: 'gte', severity: 'advisory' },
      ],
      reportingFrequency: 'quarterly',
    },
  ],

  visualization: {
    primaryColor: '#3b82f6',
    secondaryColor: '#10b981',
    healthyColor: '#22c55e',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Asset Health Summary',
      context: 'asset_health',
      audience: ['manager', 'engineer', 'technician'],
      template: 'Your {assetType} "{assetName}" at {facility} {location} is currently {healthStatus}. {trendDescription} Current OEE: {oee}%. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'facility', type: 'terminology' },
        { name: 'location', type: 'terminology' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'oee', type: 'percentage' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: 'shift',
    refreshInterval: 15,
    defaultDashboard: 'plant-floor',
  },

  professionalRoles: [
    // Operator Roles
    {
      id: 'team_member',
      category: 'operator' as const,
      name: 'Team Member',
      shortName: 'TM',
      description: 'Production team member on the line',
      responsibilities: ['Standard work', 'Quality checks', 'Andon response'],
      focusAreas: ['Safety', 'Quality', 'Standardized work'],
      explanationStyle: {
        depth: 'basic' as const,
        includeMetrics: false,
        includeCosts: false,
        includeRootCause: false,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'simple' as const,
        focusOn: ['action_items', 'safety'] as const,
      },
      decisionAuthority: ['Andon pulls', 'Minor adjustments'],
      reportingTo: 'Group Leader',
      icon: 'User',
    },
    // Supervisor Roles
    {
      id: 'group_leader',
      category: 'supervisor' as const,
      name: 'Group Leader',
      shortName: 'GL',
      description: 'First-line supervisor for production area',
      responsibilities: ['Team leadership', 'Problem solving', 'Standard work'],
      focusAreas: ['Safety', 'Quality', 'Productivity', 'Team development'],
      explanationStyle: {
        depth: 'practical' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'practical' as const,
        focusOn: ['action_items', 'safety', 'root_cause'] as const,
      },
      decisionAuthority: ['Line stops', 'Overtime', 'Minor repairs'],
      reportingTo: 'Assistant Manager',
      icon: 'Users',
    },
    // SME Roles
    {
      id: 'production_engineering_specialist',
      category: 'sme' as const,
      name: 'Production Engineering Specialist',
      shortName: 'PES',
      description: 'Develops and optimizes manufacturing processes for body-in-white, paint, and final assembly operations',
      responsibilities: [
        'Process FMEA development and maintenance',
        'Cycle time optimization and line balancing',
        'New model launch readiness (APQP)',
        'Tooling and fixture design review',
        'Continuous improvement (kaizen) facilitation',
      ],
      focusAreas: ['oee', 'cycle_time', 'process_capability', 'takt_attainment', 'first_time_through'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'action_items', 'business_impact'] as const,
      },
      decisionAuthority: ['process_parameters', 'tooling_modifications', 'cycle_time_targets', 'equipment_specifications'],
      reportingTo: 'Production Engineering Manager',
      icon: 'Settings',
    },
    // Operations Roles
    {
      id: 'plant_operations_manager',
      category: 'operator' as const,
      name: 'Plant Operations Manager',
      shortName: 'POM',
      description: 'Oversees daily plant output across stamping, body, paint, and assembly shops to meet volume and quality targets',
      responsibilities: [
        'Daily production scheduling and volume attainment',
        'Cross-shop coordination (stamping, body, paint, assembly)',
        'Downtime escalation and recovery planning',
        'Budget management and cost-per-unit tracking',
        'Headcount planning and shift management',
      ],
      focusAreas: ['jpph', 'oee', 'downtime_minutes', 'cost_per_unit', 'safety_incidents', 'on_time_delivery'],
      explanationStyle: {
        depth: 'high_level' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive' as const,
        focusOn: ['business_impact', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['production_schedule', 'overtime_authorization', 'capital_expenditure', 'staffing_levels'],
      reportingTo: 'VP Manufacturing',
      icon: 'Building',
    },
    // Technician Roles
    {
      id: 'maintenance_tech',
      category: 'technician' as const,
      name: 'Maintenance Technician / Millwright',
      shortName: 'Maint Tech',
      description: 'Multi-skilled maintenance technician handling mechanical, hydraulic, and pneumatic repairs across all shop areas',
      responsibilities: [
        'Preventive maintenance execution on robots, presses, and conveyors',
        'Breakdown troubleshooting and emergency repair',
        'Precision alignment and mechanical fitting',
        'Hydraulic and pneumatic system maintenance',
        'Work order documentation and parts requisition',
      ],
      focusAreas: ['equipment_reliability', 'pm_completion', 'mttr', 'skill_development', 'safety_compliance'],
      explanationStyle: {
        depth: 'technical' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'technical' as const,
        focusOn: ['technical_details', 'root_cause', 'action_items'] as const,
      },
      decisionAuthority: ['repair_methods', 'parts_ordering', 'immediate_safety_stops'],
      reportingTo: 'Maintenance Supervisor',
      icon: 'Wrench',
    },
    // Analyst Roles
    {
      id: 'quality_assurance_engineer',
      category: 'analyst' as const,
      name: 'Quality Assurance Engineer',
      shortName: 'QAE',
      description: 'Monitors and analyzes quality data across weld, paint, and assembly processes to drive zero-defect targets',
      responsibilities: [
        'Statistical process control (SPC) chart monitoring',
        'Measurement system analysis (MSA / GR&R studies)',
        'Customer complaint investigation and 8D reports',
        'Control plan development and audit',
        'Supplier quality coordination',
      ],
      focusAreas: ['ftt', 'dpu', 'r_100', 'cpk', 'warranty_ppm', 'scrap_rate'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['technical_details', 'root_cause', 'business_impact'] as const,
      },
      decisionAuthority: ['quality_holds', 'containment_actions', 'process_capability_limits', 'gauge_approval'],
      reportingTo: 'Quality Manager',
      icon: 'CheckSquare',
    },
    // Compliance Roles
    {
      id: 'iatf_compliance_auditor',
      category: 'analyst' as const,
      name: 'IATF Compliance Auditor',
      shortName: 'ICA',
      description: 'Ensures plant-wide adherence to IATF 16949, customer-specific requirements, and environmental/safety standards',
      responsibilities: [
        'Internal audit planning and execution (IATF 16949)',
        'Corrective action verification and effectiveness review',
        'Customer-specific requirement (CSR) gap analysis',
        'Management review preparation and reporting',
        'Regulatory compliance tracking (ISO 14001, OSHA)',
      ],
      focusAreas: ['audit_findings', 'corrective_action_closure', 'csr_compliance', 'management_review_actions', 'risk_register'],
      explanationStyle: {
        depth: 'detailed' as const,
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: true,
        vocabularyLevel: 'professional' as const,
        focusOn: ['compliance', 'risk', 'action_items'] as const,
      },
      decisionAuthority: ['audit_scheduling', 'nonconformance_classification', 'corrective_action_approval', 'certification_readiness'],
      reportingTo: 'Quality Director',
      icon: 'Shield',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Automotive Manufacturing Equipment)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'stamping_parts',
        name: 'Stamping Press Components',
        description: 'Spare parts for stamping presses, dies, and transfer systems',
        parts: [
          {
            id: 'STAMP-CLB-001',
            partNumber: 'ORTLINGHAUS-0086-100',
            name: 'Clutch/Brake Lining Set',
            description: 'Friction linings for press clutch-brake assembly, wet-type',
            category: 'stamping_parts',
            manufacturer: 'Ortlinghaus',
            unitCost: 4200,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['stamping_press'],
            failureModes: ['clutch_slip', 'brake_drag', 'friction_material_wear'],
            averageLifespan: { value: 2000000, unit: 'cycles' },
            specifications: { type: 'Wet friction', diameter: '800mm' },
          },
          {
            id: 'STAMP-DIE-001',
            partNumber: 'CUSTOM-DIE-FENDER-LH',
            name: 'Progressive Die Set - Fender',
            description: 'Multi-station progressive die for front fender stamping',
            category: 'stamping_parts',
            manufacturer: 'Ogihara',
            unitCost: 185000,
            currency: 'USD',
            leadTime: { value: 16, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['stamping_press'],
            failureModes: ['die_wear', 'galling', 'cracking', 'misalignment'],
            averageLifespan: { value: 500000, unit: 'strokes' },
            specifications: { material: 'D2 tool steel', stations: 6 },
          },
          {
            id: 'STAMP-CYL-001',
            partNumber: 'HYDR-CB-250-1200',
            name: 'Counterbalance Cylinder',
            description: 'Hydraulic counterbalance cylinder for slide weight compensation',
            category: 'stamping_parts',
            manufacturer: 'Parker',
            unitCost: 8500,
            currency: 'USD',
            leadTime: { value: 8, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['stamping_press'],
            failureModes: ['seal_leak', 'rod_scoring', 'cushion_failure'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { bore: '250mm', stroke: '1200mm', pressure: '210 bar' },
          },
        ],
      },
      {
        id: 'weld_parts',
        name: 'Welding Robot Components',
        description: 'Spot welding consumables, gun parts, and control components',
        parts: [
          {
            id: 'WELD-TIP-001',
            partNumber: 'CMW-16-2-FP',
            name: 'Electrode Tip Set (Female/Pointed)',
            description: 'Copper-chromium-zirconium electrode tips for resistance spot welding',
            category: 'weld_parts',
            manufacturer: 'CMW',
            unitCost: 45,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' as const },
            criticalityLevel: 'high' as const,
            compatibleAssetTypes: ['spot_welder'],
            failureModes: ['tip_wear', 'mushrooming', 'pitting', 'sticking'],
            averageLifespan: { value: 1500, unit: 'welds' },
            specifications: { material: 'CuCrZr (RWMA Class 2)', diameter: '16mm', taper: '4:1' },
          },
          {
            id: 'WELD-XFMR-001',
            partNumber: 'BOSCH-WTR-180',
            name: 'Weld Transformer (180 kVA)',
            description: 'Integral gun transformer for robotic spot welding',
            category: 'weld_parts',
            manufacturer: 'Bosch Rexroth',
            unitCost: 6800,
            currency: 'USD',
            leadTime: { value: 6, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['spot_welder'],
            failureModes: ['insulation_breakdown', 'core_saturation', 'overheating'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { rating: '180 kVA', ratio: '100:1', cooling: 'Water' },
          },
          {
            id: 'WELD-CABLE-001',
            partNumber: 'LEONI-WC-95-3M',
            name: 'Water-Cooled Cable Assembly',
            description: 'Flexible secondary cable for weld gun, with quick-connect fittings',
            category: 'weld_parts',
            manufacturer: 'Leoni',
            unitCost: 1250,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' as const },
            criticalityLevel: 'high' as const,
            compatibleAssetTypes: ['spot_welder'],
            failureModes: ['insulation_damage', 'water_leak', 'conductor_fatigue'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { crossSection: '95mm²', length: '3m', cooling: 'Water-cooled' },
          },
        ],
      },
      {
        id: 'paint_parts',
        name: 'Paint Robot Components',
        description: 'Atomizer, applicator, and fluid handling parts for robotic paint systems',
        parts: [
          {
            id: 'PAINT-BELL-001',
            partNumber: 'DURR-ECBS-65',
            name: 'Rotary Bell Cup (65mm)',
            description: 'High-speed rotary bell cup for electrostatic paint atomization',
            category: 'paint_parts',
            manufacturer: 'Duerr',
            unitCost: 3200,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['paint_robot'],
            failureModes: ['edge_wear', 'buildup', 'imbalance', 'cracking'],
            averageLifespan: { value: 6, unit: 'months' },
            specifications: { diameter: '65mm', maxSpeed: '70000 rpm', material: 'Titanium' },
          },
          {
            id: 'PAINT-RING-001',
            partNumber: 'DURR-SA-RING-65',
            name: 'Shaping Air Ring',
            description: 'Precision air ring that controls spray pattern width and shape',
            category: 'paint_parts',
            manufacturer: 'Duerr',
            unitCost: 1800,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' as const },
            criticalityLevel: 'high' as const,
            compatibleAssetTypes: ['paint_robot'],
            failureModes: ['orifice_blockage', 'wear', 'paint_contamination'],
            averageLifespan: { value: 12, unit: 'months' },
            specifications: { orifices: 48, airPressure: '0.5-4.0 bar' },
          },
          {
            id: 'PAINT-HOSE-001',
            partNumber: 'SAMES-PH-6-15M',
            name: 'Conductive Paint Hose (15m)',
            description: 'Antistatic PTFE-lined paint supply hose for color change system',
            category: 'paint_parts',
            manufacturer: 'Sames Kremlin',
            unitCost: 950,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' as const },
            criticalityLevel: 'high' as const,
            compatibleAssetTypes: ['paint_robot'],
            failureModes: ['liner_delamination', 'blockage', 'fitting_leak'],
            averageLifespan: { value: 24, unit: 'months' },
            specifications: { innerDiameter: '6mm', length: '15m', material: 'PTFE lined, conductive' },
          },
        ],
      },
      {
        id: 'assembly_parts',
        name: 'Assembly Line Components',
        description: 'Tooling, fixtures, and fastening equipment for final assembly',
        parts: [
          {
            id: 'ASSY-TORQ-001',
            partNumber: 'ATLAS-QST50-140',
            name: 'DC Electric Torque Tool (50-140 Nm)',
            description: 'Transducerized DC electric nutrunner for safety-critical joints',
            category: 'assembly_parts',
            manufacturer: 'Atlas Copco',
            unitCost: 4500,
            currency: 'USD',
            leadTime: { value: 3, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['test_equipment'],
            failureModes: ['transducer_drift', 'motor_wear', 'gearbox_failure'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { torqueRange: '50-140 Nm', speed: '40 rpm', type: 'DC Transducerized' },
          },
          {
            id: 'ASSY-FIX-001',
            partNumber: 'CUSTOM-FIXTURE-PIN-12',
            name: 'Locating Fixture Pin (Hardened)',
            description: 'Precision hardened locating pin for body-on-frame fixture',
            category: 'assembly_parts',
            manufacturer: 'Carr Lane',
            unitCost: 85,
            currency: 'USD',
            leadTime: { value: 5, unit: 'days' as const },
            criticalityLevel: 'essential' as const,
            compatibleAssetTypes: ['assembly_robot', 'test_equipment'],
            failureModes: ['wear', 'bending', 'galling'],
            averageLifespan: { value: 100000, unit: 'cycles' },
            specifications: { diameter: '12mm', material: 'M2 HSS hardened', tolerance: '±0.005mm' },
          },
          {
            id: 'ASSY-BELT-001',
            partNumber: 'HABASIT-FAB-8E',
            name: 'Assembly Conveyor Belt',
            description: 'Fabric conveyor belt for body skid transport, flame-retardant',
            category: 'assembly_parts',
            manufacturer: 'Habasit',
            unitCost: 3200,
            currency: 'USD',
            leadTime: { value: 3, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['conveyor_system'],
            failureModes: ['belt_wear', 'edge_fraying', 'splice_failure', 'tracking_drift'],
            averageLifespan: { value: 3, unit: 'years' },
            specifications: { width: '800mm', type: 'Fabric reinforced PVC', flameRating: 'FMVSS 302' },
          },
        ],
      },
      {
        id: 'test_bench_parts',
        name: 'Test Bench Components',
        description: 'Sensors, actuators, and data acquisition for end-of-line testing',
        parts: [
          {
            id: 'TEST-LC-001',
            partNumber: 'HBM-U10M-50KN',
            name: 'Load Cell (50 kN)',
            description: 'Precision force transducer for brake and suspension test stands',
            category: 'test_bench_parts',
            manufacturer: 'HBM',
            unitCost: 2800,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['test_equipment'],
            failureModes: ['drift', 'overload_damage', 'cable_fault', 'zero_shift'],
            averageLifespan: { value: 5, unit: 'years' },
            specifications: { capacity: '50 kN', accuracy: '0.02%', output: '2 mV/V' },
          },
          {
            id: 'TEST-SENS-001',
            partNumber: 'KISTLER-5015A',
            name: 'Charge Amplifier / Sensor Conditioner',
            description: 'Multi-channel charge amplifier for piezoelectric vibration sensors',
            category: 'test_bench_parts',
            manufacturer: 'Kistler',
            unitCost: 3500,
            currency: 'USD',
            leadTime: { value: 3, unit: 'weeks' as const },
            criticalityLevel: 'high' as const,
            compatibleAssetTypes: ['test_equipment'],
            failureModes: ['calibration_drift', 'channel_failure', 'noise_interference'],
            averageLifespan: { value: 7, unit: 'years' },
            specifications: { channels: 4, bandwidth: '200 kHz', inputType: 'Piezoelectric' },
          },
          {
            id: 'TEST-DAQ-001',
            partNumber: 'NI-PXIE-4309',
            name: 'Data Acquisition Card (24-bit)',
            description: 'High-resolution simultaneous sampling DAQ card for test bench signals',
            category: 'test_bench_parts',
            manufacturer: 'National Instruments',
            unitCost: 5200,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['test_equipment'],
            failureModes: ['channel_failure', 'firmware_corruption', 'connector_wear'],
            averageLifespan: { value: 8, unit: 'years' },
            specifications: { resolution: '24-bit', channels: 32, sampleRate: '1 MS/s per channel' },
          },
        ],
      },
      {
        id: 'agv_parts',
        name: 'AGV Components',
        description: 'Batteries, drive systems, and navigation hardware for automated guided vehicles',
        parts: [
          {
            id: 'AGV-BATT-001',
            partNumber: 'ENERSYS-24V-375AH',
            name: 'AGV Battery Pack (24V / 375Ah)',
            description: 'Deep-cycle lead-acid battery for material-handling AGVs',
            category: 'agv_parts',
            manufacturer: 'EnerSys',
            unitCost: 4800,
            currency: 'USD',
            leadTime: { value: 3, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['conveyor_system'],
            failureModes: ['capacity_degradation', 'cell_failure', 'sulfation', 'thermal_runaway'],
            averageLifespan: { value: 4, unit: 'years' },
            specifications: { voltage: '24V', capacity: '375Ah', type: 'Sealed lead-acid' },
          },
          {
            id: 'AGV-WHEEL-001',
            partNumber: 'BLICKLE-GSTH-200',
            name: 'Polyurethane Drive Wheel (200mm)',
            description: 'High-traction polyurethane drive wheel for AGV traction motor',
            category: 'agv_parts',
            manufacturer: 'Blickle',
            unitCost: 650,
            currency: 'USD',
            leadTime: { value: 2, unit: 'weeks' as const },
            criticalityLevel: 'high' as const,
            compatibleAssetTypes: ['conveyor_system'],
            failureModes: ['tread_wear', 'chunking', 'flat_spots', 'bearing_failure'],
            averageLifespan: { value: 18, unit: 'months' },
            specifications: { diameter: '200mm', material: 'Polyurethane 92A', loadRating: '1200 kg' },
          },
          {
            id: 'AGV-NAV-001',
            partNumber: 'SICK-NAV350',
            name: 'Navigation / Localization Sensor',
            description: 'Laser-based contour navigation sensor for AGV position tracking',
            category: 'agv_parts',
            manufacturer: 'SICK',
            unitCost: 7200,
            currency: 'USD',
            leadTime: { value: 4, unit: 'weeks' as const },
            criticalityLevel: 'critical' as const,
            compatibleAssetTypes: ['conveyor_system'],
            failureModes: ['lens_contamination', 'motor_failure', 'encoder_drift', 'communication_fault'],
            averageLifespan: { value: 6, unit: 'years' },
            specifications: { type: 'Laser contour navigation', range: '50m', accuracy: '±5mm', scanRate: '8 Hz' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Automotive Manufacturing)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'tooling_wear_die_damage',
        name: 'Tooling Wear & Die Damage',
        description: 'Root causes related to stamping die deterioration, trim steel wear, and forming tool failures',
        icon: 'ArrowDownCircle',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'die_surface_wear',
            name: 'Die Surface Wear / Galling',
            description: 'Progressive material transfer and scoring on die surfaces leading to panel defects and dimensional drift',
            category: 'tooling_wear_die_damage',
            symptoms: [
              'Panel surface scratches or scoring marks',
              'Increasing press tonnage required',
              'Dimensional deviation from nominal',
              'Visible galling on draw beads',
              'Higher-than-normal scrap rate from stamping',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Inspect stamped panels for surface defects using stone check',
                expectedResult: 'Identify scratch patterns traceable to specific die surfaces',
                tools: ['Stoning stone', 'Inspection light', 'Magnifying loupe'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Measure press tonnage signature over production run',
                expectedResult: 'Tonnage trend increasing above baseline by >5%',
                tools: ['Press tonnage monitor', 'Historian data'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Perform CMM check on last 10 panels',
                expectedResult: 'Dimensional drift from nominal indicates die wear zone',
                tools: ['CMM', 'Check fixtures'],
                duration: 45,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Apply TiN or TD coating to high-wear draw surfaces',
              'Schedule die polishing at defined stroke intervals',
              'Monitor tonnage trends and set alert thresholds',
              'Use proper blank lubrication type and volume',
              'Implement die maintenance tracking per stroke count',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['die_surfaces', 'draw_beads', 'trim_steels', 'form_steels'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 4, typical: 16, max: 48, unit: 'hours' as const },
            costRange: { min: 2000, max: 50000, currency: 'USD' },
            industryReferences: ['IATF 16949 Section 8.5.1.6', 'NADCA Die Maintenance Standards'],
          },
          {
            id: 'die_cracking',
            name: 'Die Cracking / Fracture',
            description: 'Stress fractures in die blocks due to fatigue, overload, or heat checking',
            category: 'tooling_wear_die_damage',
            symptoms: [
              'Visible crack lines on die surface',
              'Flash or burrs on stamped parts',
              'Abnormal press sound during stroke',
              'Sudden tonnage spike followed by drop',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform dye-penetrant inspection on suspected die areas',
                expectedResult: 'Crack indications visible under UV light',
                tools: ['Dye penetrant kit', 'UV lamp'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Review press tonnage logs for overload events',
                expectedResult: 'Identify if tonnage exceeded die rating',
                tools: ['Press monitoring system'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Monitor press tonnage versus die rating',
              'Implement thermal management for high-strength steel forming',
              'Schedule regular NDE inspections at defined intervals',
              'Track die stroke count and compare to design life',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['die_blocks', 'punch_inserts', 'form_steels'],
            requiredExpertise: ['sme' as const],
            estimatedResolutionTime: { min: 24, typical: 80, max: 240, unit: 'hours' as const },
            costRange: { min: 10000, max: 200000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'robot_automation_failures',
        name: 'Robot / Automation Failures',
        description: 'Root causes related to industrial robots, servo drives, and automated cell equipment',
        icon: 'Bot',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'servo_fault',
            name: 'Servo Motor / Drive Fault',
            description: 'Servo motor or amplifier failure causing robot axis stoppage or position errors',
            category: 'robot_automation_failures',
            symptoms: [
              'Servo alarm fault codes on teach pendant',
              'Position deviation or overshoot',
              'Overload or overcurrent alarms',
              'Abnormal motor noise or vibration',
              'Axis unable to reach taught position',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Read fault codes from teach pendant and controller diagnostics',
                expectedResult: 'Identify specific servo alarm (overcurrent, encoder, overheating)',
                tools: ['Teach pendant', 'Controller diagnostic software'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Measure motor winding resistance and insulation',
                expectedResult: 'Phase-to-phase resistance balanced; insulation >1 MOhm',
                tools: ['Digital multimeter', 'Megohmmeter'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Check encoder feedback by manually jogging axis',
                expectedResult: 'Encoder counts track smoothly without dropout',
                tools: ['Teach pendant', 'Oscilloscope'],
                duration: 15,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Perform regular PM including fan filter cleaning and cable inspection',
              'Monitor servo current trends via controller historian',
              'Replace batteries for encoder backup at defined intervals',
              'Keep ambient temperature within OEM specification',
              'Track robot hours and schedule overhaul per OEM schedule',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['servo_motor', 'servo_amplifier', 'encoder', 'brake'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 5000, max: 25000, currency: 'USD' },
            industryReferences: ['Fanuc Maintenance Manual', 'ABB Robot Service Guide'],
          },
          {
            id: 'robot_collision',
            name: 'Robot Collision / Path Deviation',
            description: 'Unexpected contact between robot end-effector and fixtures, parts, or other robots',
            category: 'robot_automation_failures',
            symptoms: [
              'Collision detection alarm',
              'Bent or damaged end-effector tooling',
              'TCP (Tool Center Point) offset after restart',
              'Deformed parts or fixtures',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review collision alarm log and identify axis torques at fault time',
                expectedResult: 'Identify which axis triggered collision limit',
                tools: ['Controller log', 'Teach pendant'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Verify TCP calibration using fixed reference point',
                expectedResult: 'TCP deviation from calibrated position within tolerance',
                tools: ['TCP calibration pointer', 'Dial indicator'],
                duration: 30,
                skillLevel: 'advanced' as const,
              },
            ],
            preventiveActions: [
              'Implement periodic TCP calibration checks',
              'Use electronic TCP monitoring (masterpoint check)',
              'Review programs after body style changes',
              'Ensure fixture clamps are verified before robot entry',
            ],
            typicalProbability: 0.06,
            affectedComponents: ['end_effector', 'tool_flange', 'fixtures', 'weld_gun'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 16, unit: 'hours' as const },
            costRange: { min: 500, max: 15000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'quality_defects',
        name: 'Quality Defects (Paint, Weld, Assembly)',
        description: 'Root causes of in-process quality failures across paint, weld, and assembly operations',
        icon: 'AlertTriangle',
        color: '#dc2626',
        rootCauses: [
          {
            id: 'paint_defect_seeds',
            name: 'Paint Defects - Seeds / Dirt Nibs',
            description: 'Particulate contamination embedded in paint film causing visible surface blemishes',
            category: 'quality_defects',
            symptoms: [
              'Visible raised spots or nibs in clear coat',
              'Seeds-per-unit count exceeding control limit',
              'Higher sand-and-polish rework rate',
              'Defect concentration on horizontal surfaces',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Perform particle tape-lift test in booth zones',
                expectedResult: 'Identify particle source (fiber, paint flake, metal, organic)',
                tools: ['Particle tape kit', 'Microscope'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Review booth air balance and filter differential pressure',
                expectedResult: 'Air flow within spec, filter dP below change threshold',
                tools: ['Manometer', 'Anemometer'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Inspect paint supply filters and color changer seals',
                expectedResult: 'No ruptured filters or leaking seals',
                tools: ['Flashlight', 'Magnifying loupe'],
                duration: 25,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Maintain positive pressure in paint booth at all times',
              'Replace ceiling and intake filters on schedule',
              'Enforce lint-free suit and tacky mat protocols',
              'Flush paint lines during color change per SOP',
              'Monitor booth particle counts with laser particle counter',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['booth_filters', 'paint_supply', 'color_changer', 'air_handling'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 500, max: 5000, currency: 'USD' },
          },
          {
            id: 'weld_quality_failure',
            name: 'Weld Nugget Undersize / Expulsion',
            description: 'Spot welds failing to meet minimum nugget diameter or exhibiting excessive expulsion',
            category: 'quality_defects',
            symptoms: [
              'Destructive test showing undersized nuggets',
              'Ultrasonic test NQR (Not Quite Right) flags',
              'Visible weld expulsion (spatter) on panels',
              'Adaptive weld controller compensating above limits',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Pull chisel-test coupons from suspect gun',
                expectedResult: 'Nugget diameter below minimum (e.g., 4*sqrt(t) rule)',
                tools: ['Chisel', 'Calipers', 'Coupon material'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Measure electrode tip face diameter and condition',
                expectedResult: 'Tip face diameter exceeds max allowed (mushrooming)',
                tools: ['Tip gauge', 'Micrometer'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Review weld controller data for current, time, and force',
                expectedResult: 'Identify parameter drift from baseline schedule',
                tools: ['Weld controller HMI', 'Data historian'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Implement adaptive weld control with real-time monitoring',
              'Schedule tip dress cycles per OEM weld count spec',
              'Perform periodic cap tip change at defined intervals',
              'Maintain water cooling temperature and flow rate',
              'Run daily audit welds with destructive testing per control plan',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['electrode_tips', 'weld_transformer', 'weld_gun', 'cooling_system'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' as const },
            costRange: { min: 100, max: 3000, currency: 'USD' },
            industryReferences: ['AWS D8.1M', 'Customer Weld Standards (OEM-specific)'],
          },
          {
            id: 'torque_failure',
            name: 'Assembly Torque Rejects',
            description: 'Fastening operations failing torque-angle specification on safety-critical joints',
            category: 'quality_defects',
            symptoms: [
              'Torque tool reject signal (NOK)',
              'Torque-angle curve shape anomalies',
              'Increasing reject rate on specific station',
              'Joint audit failures on confirmation torque',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review reject data and torque-angle curves from controller',
                expectedResult: 'Identify failure mode: angle too high, torque undershoot, or rundown anomaly',
                tools: ['Torque controller software', 'Data export'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Verify torque tool calibration against reference transducer',
                expectedResult: 'Tool reading within ±2% of reference',
                tools: ['Reference torque transducer', 'Calibration fixture'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Calibrate torque tools per IATF schedule',
              'Monitor SPC charts for torque and angle trends',
              'Inspect fastener supply for mixed or damaged parts',
              'Verify joint components (clip, nut, washer) are present and correct',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['torque_tool', 'fastener_supply', 'joint_components'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 15, typical: 60, max: 240, unit: 'minutes' as const },
            costRange: { min: 50, max: 2000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'conveyor_material_handling',
        name: 'Conveyor & Material Handling Issues',
        description: 'Root causes related to conveyors, skid systems, overhead carriers, and AGVs',
        icon: 'ArrowRightLeft',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'chain_wear_elongation',
            name: 'Chain Wear / Elongation',
            description: 'Progressive chain stretch causing position inaccuracy, skid slippage, or chain jump',
            category: 'conveyor_material_handling',
            symptoms: [
              'Skid position overshoot at stations',
              'Carrier spacing drift exceeding tolerance',
              'Chain noise (clicking, grinding)',
              'Excessive chain sag between sprockets',
              'Increased motor current draw',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure chain pitch over 10-link sample at multiple locations',
                expectedResult: 'Elongation >3% of nominal pitch indicates replacement needed',
                tools: ['Chain wear gauge', 'Tape measure'],
                duration: 20,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Inspect sprocket teeth for hooking or wear',
                expectedResult: 'Symmetrical tooth profile, no hooking',
                tools: ['Sprocket gauge', 'Flashlight'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Check lubrication system operation and coverage',
                expectedResult: 'All lubrication points receiving oil, no dry sections',
                tools: ['Visual inspection'],
                duration: 10,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Implement automatic chain lubrication system with flow monitoring',
              'Measure chain elongation during scheduled PMs',
              'Replace chain and sprockets as matched sets',
              'Monitor motor current as proxy for chain drag',
              'Ensure proper chain tension adjustment',
            ],
            typicalProbability: 0.14,
            affectedComponents: ['conveyor_chain', 'sprockets', 'drives', 'lubrication_system'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 4, typical: 12, max: 36, unit: 'hours' as const },
            costRange: { min: 2000, max: 25000, currency: 'USD' },
          },
          {
            id: 'agv_navigation_failure',
            name: 'AGV Navigation / Positioning Failure',
            description: 'AGV unable to maintain route accuracy due to navigation sensor, reflector, or floor condition issues',
            category: 'conveyor_material_handling',
            symptoms: [
              'AGV stops with navigation fault alarm',
              'Docking position offset at load/unload stations',
              'AGV oscillating or hunting during travel',
              'Increased missed delivery windows',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check navigation sensor lens cleanliness and mounting alignment',
                expectedResult: 'Lens clean, sensor level and secure',
                tools: ['Lens cleaning kit', 'Level'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
              {
                order: 2,
                action: 'Verify reflector visibility and condition on route',
                expectedResult: 'All required reflectors visible, clean, and undamaged',
                tools: ['Reflector test card', 'Cleaning supplies'],
                duration: 30,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Review AGV diagnostic log for position confidence values',
                expectedResult: 'Position confidence above 80% throughout route',
                tools: ['AGV fleet management software'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Schedule regular lens cleaning and reflector inspection',
              'Maintain floor surface (patch holes, control dust)',
              'Recalibrate navigation map after plant layout changes',
              'Monitor AGV battery voltage during navigation',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['navigation_sensor', 'reflectors', 'floor_surface', 'odometry_encoders'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' as const },
            costRange: { min: 200, max: 8000, currency: 'USD' },
          },
        ],
      },
      {
        id: 'electrical_control_faults',
        name: 'Electrical & Control System Faults',
        description: 'Root causes related to PLCs, safety systems, power distribution, and network infrastructure',
        icon: 'Zap',
        color: '#8b5cf6',
        rootCauses: [
          {
            id: 'plc_io_fault',
            name: 'PLC I/O Module Failure',
            description: 'Input or output module failure causing loss of sensor signals or actuator control',
            category: 'electrical_control_faults',
            symptoms: [
              'Specific I/O points stuck or intermittent',
              'PLC diagnostic LED indicating module fault',
              'Sensors reading correctly but PLC not receiving signal',
              'Actuators not responding to PLC commands',
              'Communication error counters incrementing',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Check PLC module diagnostic LEDs and fault buffer',
                expectedResult: 'Identify specific module and channel with fault',
                tools: ['PLC programming software', 'Laptop'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Measure field wiring voltage at module terminals',
                expectedResult: 'Confirm signal present at module but not registering',
                tools: ['Digital multimeter'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Swap suspect module with known-good spare',
                expectedResult: 'Function restored confirms module failure',
                tools: ['Spare I/O module', 'ESD wrist strap'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Monitor PLC diagnostic counters in SCADA for early warning',
              'Maintain clean and temperature-controlled control cabinets',
              'Keep spare modules of each type in on-site inventory',
              'Perform annual I/O loop checks during major shutdowns',
              'Ensure proper grounding and shielding of field wiring',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['plc_io_module', 'field_wiring', 'terminal_blocks', 'bus_connector'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 30, typical: 120, max: 480, unit: 'minutes' as const },
            costRange: { min: 300, max: 5000, currency: 'USD' },
            industryReferences: ['IEC 61131-2', 'Siemens / Allen-Bradley module manuals'],
          },
          {
            id: 'safety_system_trip',
            name: 'Safety System Nuisance Trip',
            description: 'Safety-rated devices (light curtains, e-stops, interlocks) causing unwarranted cell stoppages',
            category: 'electrical_control_faults',
            symptoms: [
              'Frequent safety faults without visible cause',
              'Cell restart required after unexplained safety trips',
              'Specific safety zone trips at consistent times or conditions',
              'Safety relay diagnostic showing intermittent input',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review safety relay diagnostic log for trip source',
                expectedResult: 'Identify which safety input triggered the stop',
                tools: ['Safety relay diagnostic software'],
                duration: 10,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Inspect light curtain alignment and lens condition',
                expectedResult: 'Beams aligned, lenses clean, no weld spatter or debris',
                tools: ['Alignment tool', 'Cleaning supplies'],
                duration: 20,
                skillLevel: 'basic' as const,
              },
              {
                order: 3,
                action: 'Check interlock switch mounting and actuation gap',
                expectedResult: 'Switch actuated within specification, mounting secure',
                tools: ['Feeler gauge', 'Wrenches'],
                duration: 15,
                skillLevel: 'basic' as const,
              },
            ],
            preventiveActions: [
              'Clean light curtain lenses during every PM cycle',
              'Verify interlock switch alignment after any maintenance activity',
              'Shield safety devices from weld spatter and coolant',
              'Log and trend safety trips to identify repeat offenders',
              'Perform annual safety system functional test per ISO 13849',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['light_curtains', 'safety_relays', 'interlock_switches', 'e_stops'],
            requiredExpertise: ['technician' as const],
            estimatedResolutionTime: { min: 15, typical: 60, max: 240, unit: 'minutes' as const },
            costRange: { min: 100, max: 3000, currency: 'USD' },
            industryReferences: ['ISO 13849-1', 'IEC 62443'],
          },
        ],
      },
      {
        id: 'environmental_control_problems',
        name: 'Environmental Control Problems (Paint Booth)',
        description: 'Root causes related to temperature, humidity, and air quality control in paint shop operations',
        icon: 'Thermometer',
        color: '#10b981',
        rootCauses: [
          {
            id: 'booth_temp_humidity',
            name: 'Booth Temperature / Humidity Excursion',
            description: 'Paint booth temperature or humidity drifting outside specification causing finish defects',
            category: 'environmental_control_problems',
            symptoms: [
              'Orange peel or solvent pop in paint film',
              'Sagging or runs on vertical surfaces',
              'Slow flash-off between coats',
              'Booth HVAC alarm on temperature or humidity',
              'Increased polish/rework rate',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review booth temperature and humidity trending from BMS',
                expectedResult: 'Identify when excursion started and magnitude of deviation',
                tools: ['Building management system (BMS)', 'Data historian'],
                duration: 15,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Check HVAC chiller and humidifier operation',
                expectedResult: 'All units running, set points correct, no fault alarms',
                tools: ['HVAC control panel'],
                duration: 20,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 3,
                action: 'Verify booth air supply and exhaust balance',
                expectedResult: 'Positive pressure maintained; supply/exhaust within 10% of design',
                tools: ['Anemometer', 'Manometer'],
                duration: 25,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Monitor booth conditions with SPC trending and automated alerts',
              'Perform seasonal HVAC commissioning (spring/fall)',
              'Maintain chiller, humidifier, and heater per OEM schedule',
              'Calibrate booth sensors quarterly',
              'Adjust paint formulation parameters for seasonal extremes',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['hvac_system', 'chillers', 'humidifiers', 'heaters', 'booth_sensors'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 1, typical: 4, max: 12, unit: 'hours' as const },
            costRange: { min: 500, max: 15000, currency: 'USD' },
            industryReferences: ['ASHRAE Handbook', 'Paint Supplier Technical Data Sheets'],
          },
          {
            id: 'oven_cure_failure',
            name: 'Oven Cure Temperature Failure',
            description: 'Paint bake oven unable to maintain cure temperature profile, causing adhesion or durability issues',
            category: 'environmental_control_problems',
            symptoms: [
              'Oven temperature below setpoint in one or more zones',
              'Cross-hatch adhesion test failures on baked panels',
              'Solvent retention detected in film',
              'Gloss readings below specification',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Run oven temperature survey with data logger puck',
                expectedResult: 'All zones within ±5C of cure window for required duration',
                tools: ['Oven data logger (Datapaq/ECD)', 'Thermal barrier'],
                duration: 60,
                skillLevel: 'intermediate' as const,
              },
              {
                order: 2,
                action: 'Inspect burner operation and combustion',
                expectedResult: 'Blue flame, proper air-fuel ratio, no sooting',
                tools: ['Combustion analyzer', 'Visual inspection'],
                duration: 30,
                skillLevel: 'intermediate' as const,
              },
            ],
            preventiveActions: [
              'Perform monthly oven surveys with data logger',
              'Calibrate oven thermocouples quarterly',
              'Clean oven recirculation fans and ducts per schedule',
              'Maintain combustion equipment per burner OEM specification',
              'Track oven energy consumption trends as health proxy',
            ],
            typicalProbability: 0.07,
            affectedComponents: ['burners', 'thermocouples', 'recirculation_fans', 'dampers', 'ductwork'],
            requiredExpertise: ['technician' as const, 'sme' as const],
            estimatedResolutionTime: { min: 2, typical: 8, max: 24, unit: 'hours' as const },
            costRange: { min: 1000, max: 20000, currency: 'USD' },
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'weld_degradation',
        name: 'Weld Quality Degradation Cascade',
        description: 'Progressive weld quality decline from tip wear through undersized nuggets to structural weld failures',
        sequence: [
          'Electrode tip face diameter increases (mushrooming)',
          'Current density decreases at tip-sheet interface',
          'Weld nugget diameter shrinks below minimum spec',
          'Adaptive controller compensates by increasing current',
          'Expulsion (spatter) begins as current exceeds material limit',
          'Weld failures detected at audit or destructive test',
        ],
        indicators: [
          'Rising weld current trend in controller data',
          'Tip dress frequency increasing above baseline',
          'Ultrasonic audit flags (NQR rate climbing)',
          'Adaptive step-up counter incrementing faster than normal',
        ],
        preventionStrategy: 'Adaptive weld control with cap tip management, proactive tip dress scheduling, and real-time nugget estimation monitoring',
      },
      {
        id: 'press_progressive_failure',
        name: 'Stamping Press Progressive Failure',
        description: 'Die wear progressing to dimensional drift, then scrap increase, then line stoppage',
        sequence: [
          'Die surface friction increases due to coating wear',
          'Panel dimensions begin drifting toward upper/lower spec limits',
          'Tonnage monitor shows gradual increase per stroke',
          'Scrap rate at downstream check fixture rises above threshold',
          'Quality hold issued, line stopped for die maintenance',
        ],
        indicators: [
          'Press tonnage trending upward versus baseline',
          'CMM data showing dimensional drift on key GD&T features',
          'Scrap rate at downstream inspection climbing',
        ],
        preventionStrategy: 'Monitor press tonnage signature per stroke and correlate with downstream dimensional data; trigger die maintenance before quality limits are exceeded',
      },
      {
        id: 'paint_booth_contamination_cycle',
        name: 'Paint Booth Contamination Cycle',
        description: 'Gradual buildup of particulate sources leading to escalating seeds-per-unit defects',
        sequence: [
          'Booth intake filters load with ambient dust',
          'Filter differential pressure rises, reducing air flow',
          'Positive pressure drops, allowing ingress of plant air',
          'Seeds-per-unit count begins climbing',
          'Rework rate increases, eventually triggering production constraint',
        ],
        indicators: [
          'Filter dP gauges approaching change limits',
          'Booth positive pressure trending downward',
          'Seeds-per-unit SPC chart trending upward',
        ],
        preventionStrategy: 'Automated filter dP monitoring with predictive replacement scheduling and real-time booth pressure alarming',
      },
    ],
  },

  laborRates: {
    roles: [
      { roleId: 'team_member', name: 'Team Member', hourlyRate: 35, currency: 'USD', certifications: [] },
      { roleId: 'maintenance_tech', name: 'Maintenance Technician', hourlyRate: 55, currency: 'USD', certifications: ['Multi-Skilled'] },
      { roleId: 'robot_tech', name: 'Robot Technician', hourlyRate: 65, currency: 'USD', certifications: ['OEM Certified'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
